/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import styles from './ThresholdsModal.module.css';
import { FloModal, FloModalBodyProps, FloSlider, TagList } from 'components';
import { Trans, useTranslation } from 'react-i18next';
import Message from 'containers/common/Message';
import { Col, Row } from 'react-bootstrap';
import { Location } from 'model';
import ContentLoader from "react-content-loader";
import { ThresholdSettings } from 'model/Threshold';

interface ThresholdsModalBodyProps extends FloModalBodyProps {
  locations:   Location[];
  thresholds:  ThresholdSettings;
  onChange:    (thresholds: ThresholdSettings) => void;
  onSave:      (locationIds: string[], settings: ThresholdSettings) => void;
}

const ThresholdsModalBody: React.FC<ThresholdsModalBodyProps> = React.memo(({
  locations,
  thresholds,
  onChange,
  onSave,
  bindSubmitForm,
}) => {
  const { t } = useTranslation();

  bindSubmitForm(async () => {
    onSave(locations.map(l => l.id as string), thresholds);
  });

  return (
    <>
      <Row className={styles.bodyContainer}>
        <Col xs="12">
          <Row>
            <Col xs="12">
              <Message />
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <div>
                <div className={styles.locationInfo}>
                  <Trans
                    i18nKey="set-thresholds-location-info-title"
                    count={locations.length}
                    components={{ span: <span /> }}
                  />
                </div>
                <TagList
                  list = {locations.map(l => l.nickname || l.address || '')}
                  max = {7}
                />
              </div>
                <div className={styles.sliderGroup}>
                  <div className={styles.title}>{t('Gallons')}</div>
                  <FloSlider
                    min       = {thresholds.volume.defaults.min}
                    max       = {thresholds.volume.defaults.max}
                    step      = {thresholds.volume.defaults.step}
                    value     = {thresholds.volume.value}
                    onChange  = {(value) => onChange({
                      ...thresholds,
                      volume: {
                        ...thresholds.volume,
                        value
                      }
                    })}
                  />
                </div>
                <div className={styles.sliderGroup}>
                  <div className={styles.title}>{t('Minutes')}</div>
                  <FloSlider
                    min       = {thresholds.duration.defaults.min}
                    max       = {thresholds.duration.defaults.max}
                    step      = {thresholds.duration.defaults.step}
                    value     = {thresholds.duration.value}
                    onChange  = {(value) => onChange({
                      ...thresholds,
                      duration: {
                        ...thresholds.duration,
                        value
                      }
                    })}
                  />
                </div>
                <div className={styles.sliderGroup}>
                  <div className={styles.title}>{t('Maximum Flow Rate')}</div>
                  <FloSlider
                    min       = {thresholds.flowRate.defaults.min}
                    max       = {thresholds.flowRate.defaults.max}
                    step      = {thresholds.flowRate.defaults.step}
                    value     = {thresholds.flowRate.value}
                    onChange  = {(value) => onChange({
                      ...thresholds,
                      flowRate: {
                        ...thresholds.flowRate,
                        value
                      }
                    })}
                  />
                </div>
                <div className={styles.sliderGroup}>
                  <div className={styles.title}>{t('Maximum Flow Rate Duration')}</div>
                  <FloSlider
                    min       = {thresholds.flowRateDuration.defaults.min}
                    max       = {thresholds.flowRateDuration.defaults.max}
                    step      = {thresholds.flowRateDuration.defaults.step}
                    value     = {thresholds.flowRateDuration.value}
                    onChange  = {(value) => onChange({
                      ...thresholds,
                      flowRateDuration: {
                        ...thresholds.flowRateDuration,
                        value
                      }
                    })}
                  />
                </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
})


const ThresholdsSkeleton: React.FC<{}> = () => (
  <ContentLoader
    height={400}
    width={'100%'}
    speed={3}
  >
    <rect x="0" y="0" rx="3" ry="3" width="200" height="20" />

    <rect x="0" y="50" rx="3" ry="3" width="80" height="24" />
    <rect x="90" y="50" rx="3" ry="3" width="110" height="24" />
    <rect x="210" y="50" rx="3" ry="3" width="60" height="24" />
    <rect x="280" y="50" rx="3" ry="3" width="130" height="24" />


    <rect x="0" y="120" rx="3" ry="3" width="140" height="20" />
    <rect x="0" y="150" rx="3" ry="3" width={'100%'} height="30" />

    <rect x="0" y="210" rx="3" ry="3" width="140" height="20" />
    <rect x="0" y="240" rx="3" ry="3" width={'100%'} height="30" />


  </ContentLoader>
)

type ThresholdsModalProps = {
  show:                     boolean,
  onHide:                   () => void,
  isSubmitting:             boolean,
  locations:                Location[],
  thresholds:               ThresholdSettings | null,
  fetchThresholdSettings:   (locations: Location[]) => void,
  updateThresholdsSettings: (settings: ThresholdSettings) => void,
  saveThresholdSettings:    (locationIds: string[], settings: ThresholdSettings) => void,
  clearMessage:             () => void,
  hasError:                 boolean,
}

export const ThresholdsModal: React.FC<ThresholdsModalProps> = React.memo(({
  show,
  locations,
  thresholds,
  fetchThresholdSettings,
  updateThresholdsSettings,
  saveThresholdSettings,
  clearMessage,
  hasError,
  ...rest
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (show) {
      clearMessage();
      fetchThresholdSettings(locations);
    }
  }, [show]);


  return (
    <FloModal
      {...rest}
      show            = {show}
      name            = "set-thresholds-modal"
      title           = {t('Set Thresholds')}
      className       = {styles.root}
      disableButtons  = {thresholds === null || rest.isSubmitting}
      body            = { (props) => thresholds ?
        <ThresholdsModalBody
          {...props}
          locations   = {locations}
          thresholds  = {thresholds}
          onChange    = {thresholds => updateThresholdsSettings(thresholds)}
          onSave      = {saveThresholdSettings}
        />
        :
        <ThresholdsSkeleton />
      }
    />
  )
})
